import noop from 'lodash-es/noop'
import { useEffect, useRef, useState } from 'react'
import { LuArrowDownAZ, LuArrowUpAZ, LuSearch, LuSlidersHorizontal } from 'react-icons/lu'
import { useImmerReducer } from 'use-immer'

import type { FilterAction, Filters, SearchSectionProps } from '@/feature/observatory/filters'
import { CheckboxInput } from '@/components/CheckboxInput'

import styles from './SearchSection.module.css'

export const SearchSection = <TToggle extends string, TSort extends string>({
  search = '',
  onSearchChanged = noop,
  filters = { toggles: {} } as Filters<TToggle, TSort>,
  onFilterChanged = noop,
  sortOptions = [],
}: SearchSectionProps<TToggle, TSort>) => {
  const dialogElement = useRef<HTMLDialogElement>(null)
  const buttonElement = useRef<HTMLButtonElement>(null)

  const [popoverPosition, setPopoverPosition] = useState(
    buttonElement.current?.getBoundingClientRect() ?? { bottom: 0, right: 0 },
  )

  const { bottom, right } = popoverPosition

  const [filterState, dispatchFilterState] = useImmerReducer<
    Filters<TToggle, TSort>,
    FilterAction<TToggle, TSort>
  >((draft, action) => {
    switch (action.type) {
      case 'TOGGLE_FILTER':
        {
          const toggles = draft.toggles as Record<TToggle, boolean>
          toggles[action.filter] = !toggles[action.filter]
        }
        break
      case 'SORT':
        draft.sortOn = action.sortOn as unknown as typeof draft.sortOn
        break
      case 'DIRECTION':
        draft.sortDirection = (action.direction ?? draft.sortDirection === 'asc') ? 'desc' : 'asc'
        break
      default:
        break
    }
  }, filters)

  useEffect(() => {
    onFilterChanged(filterState)
  }, [filterState, onFilterChanged])

  const showPopover = () => {
    const pos = buttonElement.current?.getBoundingClientRect()
    if (pos) {
      setPopoverPosition(pos)
    }
    dialogElement.current?.showPopover()
  }

  return (
    <div className={styles.searchSection}>
      <input
        type="text"
        placeholder="Search"
        className={styles.searchInput}
        value={search}
        onChange={(e) => onSearchChanged(e.target.value)}
      />
      <div className={styles.searchIcon}>
        <LuSearch size={16} />
      </div>
      <button
        type="button"
        className={styles.searchButton}
        onClick={() => showPopover()}
        ref={buttonElement}
      >
        <LuSlidersHorizontal size={16} />
      </button>
      <dialog
        className={styles.searchFilters}
        ref={dialogElement}
        style={{ top: bottom, left: right }}
        onClick={() => dialogElement.current?.close()}
        popover="auto"
      >
        <header
          className={[
            styles.header,
            Object.keys(filterState.toggles).length > 0 ? undefined : styles.hidden,
          ].join(' ')}
        >
          Filters
        </header>
        <header
          className={[styles.header, sortOptions.length > 0 ? undefined : styles.hidden].join(' ')}
        >
          Sort By
        </header>
        <div
          className={[
            styles.searchOptions,
            Object.keys(filterState.toggles).length > 0 ? undefined : styles.hidden,
          ].join(' ')}
        >
          {(Object.keys(filterState.toggles) as TToggle[]).map((filter) => (
            <CheckboxInput
              key={filter}
              checked={!!filterState.toggles[filter]}
              onChange={() => dispatchFilterState({ type: 'TOGGLE_FILTER', filter })}
            >
              {filter}
            </CheckboxInput>
          ))}
        </div>
        <div
          className={[
            styles.searchOptions,
            sortOptions.length > 0 ? undefined : styles.hidden,
          ].join(' ')}
        >
          {(sortOptions as TSort[]).map((option) => (
            <label key={option} className={styles.basicRadio}>
              <input
                type="radio"
                value={option}
                onChange={() => dispatchFilterState({ type: 'SORT', sortOn: option })}
                onClick={() => {
                  if (filterState.sortOn === option) {
                    dispatchFilterState({ type: 'DIRECTION' })
                  }
                }}
                checked={filterState.sortOn === option}
              />
              {option}

              {filterState.sortDirection === 'asc' ? (
                <LuArrowUpAZ className={styles.check} />
              ) : (
                <LuArrowDownAZ className={styles.check} />
              )}
            </label>
          ))}
        </div>
      </dialog>
    </div>
  )
}
